// MINHA INSTITUIÇÃO — pedir, vincular, moderar.
//
// Onze pontos do T2 caem nesta tela, e ela vive DENTRO de "Minha conta" porque é ele quem a põe
// lá. `T2.103`: *"pode ter coisa aqui, por exemplo, para CRIAR INSTITUIÇÃO aqui (...) tem essa
// parte de editar meus dados, que é uma outra estrutura, e tem agora as coisas do criar
// instituição, VINCULAR INSTITUIÇÃO, esse tipo de coisa."*
//
// ── TRÊS COISAS NUMA TELA SÓ, E POR QUÊ ──────────────────────────────────────────────────────
//
//   1. MEUS VÍNCULOS — onde eu já estou, e em que situação.
//   2. ENTRAR NUMA INSTITUIÇÃO — escolher da lista e pedir, como educador ou aluno (T2.74).
//   3. MODERAÇÃO — só aparece para quem é educador ATIVO em alguma instituição (T2.75, T2.78).
//
// São três passos do mesmo caminho. Separá-los em três páginas faria a pessoa procurar — e
// "procurar" é do que o T2 reclama em seis pontos diferentes.
//
// ── O QUE ESTA TELA NÃO DECIDE ───────────────────────────────────────────────────────────────
//
// Nada. Ela mostra e pede; quem autoriza é `firestore.rules`, provado por 42 casos novos em
// `prova-regras/`. Se esta tela esconder um botão que a regra permitiria, é defeito de interface;
// se ela mostrar um botão que a regra nega, o pedido volta com "você não tem permissão" e a
// pessoa lê isso — que é pior, mas não é insegurança.
//
// ── O CASO DO T2.71, QUE É O MAIS FÁCIL DE ESQUECER ─────────────────────────────────────────
//
// *"se o cara cadastra com o email, o cadastro é meio que automático, né, então não funciona."*
// Uma conta criada por provedor externo — ou criada antes de o campo existir — nunca passou pelo
// formulário e não tem tipo declarado. Para ela, a cobrança acontece AQUI, e não com um erro: com
// um pedido, no lugar em que a informação finalmente importa.

import { useCallback, useEffect, useState } from "react";
import AppUser from "../api/app-user.class";
import {
  listarInstituicoesAtivas,
  meusVinculos,
  minhasSolicitacoesDeInstituicao,
  buscarInstituicao,
  moderarVinculo,
  pedirDeNovo,
  vinculosDaInstituicao,
} from "./api";
import BuscaDeEscola from "./busca-de-escola";
import ConvitesDaEscola from "./convites-da-escola";
import {
  AVISO_INSTITUICAO_INATIVA,
  Instituicao,
  ROTULO_SITUACAO_INSTITUICAO,
  ROTULO_SITUACAO_VINCULO,
  Vinculo,
  entraNaFilaEm,
  podeModerar,
  visivelNaFila,
} from "./modelo";

import "./minha-instituicao.scss";

function MinhaInstituicao({ appUser }: { appUser: AppUser }) {
  const uid = appUser.Id();

  const [vinculos, setVinculos] = useState<Vinculo[] | null>(null);
  const [ativas, setAtivas] = useState<Instituicao[]>([]);
  const [pedidas, setPedidas] = useState<Instituicao[]>([]);
  const [fila, setFila] = useState<Record<string, Vinculo[]>>({});
  // Manual do EducaHubPlay, 09/10: qualquer educador ativo remove outro (inclusive o primeiro),
  // com registro de quem removeu e quando. Esta é a lista de quem já está, para poder remover.
  const [membros, setMembros] = useState<Record<string, Vinculo[]>>({});
  // A remoção pede confirmação NA PÁGINA (a casa proíbe a caixa do navegador): o id em confirmação.
  const [removendo, setRemovendo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  // T12.9 · as duas listas que têm os NOMES das escolas chegam separadas dos meus vínculos.
  const [nomesProntos, setNomesProntos] = useState(false);
  const [aviso, setAviso] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);


  const recarregar = useCallback(async () => {
    // ── T12.9 · CADA PEDAÇO APARECE QUANDO CHEGA, E NÃO QUANDO O ÚLTIMO CHEGA ──────────────
    //
    // Quem testou em 28/09, aos 3:38: *"no resumo da conta, a seção «Minha instituição» mostrava
    // «Carregando…» vários segundos depois de a página abrir, enquanto o restante já estava
    // pronto."*
    //
    // A CAUSA ERA UM `Promise.all`. São três leituras, e o primeiro bloco da seção — "onde eu já
    // estou" — depende de UMA delas, `meusVinculos`, que é uma consulta por `uid`. As outras duas
    // são a lista INTEIRA de instituições ativas e as minhas solicitações. Com `Promise.all`, o
    // mais rápido dos três ficava preso ao mais lento, e o "Carregando…" que ele viu era o bloco
    // esperando um dado que ele nem usa.
    //
    // Agora as três continuam partindo JUNTAS — isso nunca foi o problema — e cada uma escreve o
    // seu estado quando volta.
    const pVinculos = meusVinculos(uid);
    const pAtivas = listarInstituicoesAtivas();
    const pPedidas = minhasSolicitacoesDeInstituicao(uid);

    // Os nomes vivem nas duas listas de baixo. Enquanto elas não chegam, `nomeDa` não pode cair
    // no `id` cru: um código de banco de dados no lugar do nome da escola é pior que esperar.
    void Promise.all([pAtivas, pPedidas]).then(([a, p]) => {
      setAtivas(a);
      setPedidas(p);
      setNomesProntos(true);
    });

    const v = await pVinculos;
    setVinculos(v);

    // Escola pedida por OUTRO educador (caso A) não está nas duas listas: o nome vem do documento.
    void Promise.all([pAtivas, pPedidas]).then(async ([a, p]) => {
      const conhecidas = new Set([...a, ...p].map((i) => i.id));
      const faltam = v.map((x) => x.instituicaoId).filter((id) => !conhecidas.has(id));
      const achadas = (await Promise.all(faltam.map(buscarInstituicao))).filter(Boolean) as Instituicao[];
      if (achadas.length) setPedidas((atual) => [...atual, ...achadas.filter((x) => !atual.some((y) => y.id === x.id))]);
    });

    // A fila de cada instituição em que eu sou educador ativo. Uma leitura por instituição, e
    // não uma por vínculo: quem tem vínculo em duas escolas modera as duas, e as duas filas são
    // coisas separadas.
    const filas: Record<string, Vinculo[]> = {};
    const dentro: Record<string, Vinculo[]> = {};
    for (const meu of v.filter(podeModerar)) {
      dentro[meu.instituicaoId] = (await vinculosDaInstituicao(meu.instituicaoId, "ativo")).filter((m) => m.uid !== uid);
      // Caso F: pedido de quem foi recusado só aparece 7 dias depois da recusa.
      filas[meu.instituicaoId] = (await vinculosDaInstituicao(meu.instituicaoId, "pendente")).filter((p) =>
        visivelNaFila(p)
      );
    }
    setFila(filas);
    setMembros(dentro);
  }, [uid]);

  useEffect(() => {
    recarregar();
  }, [recarregar]);

  async function comOcupado(fn: () => Promise<{ ok: boolean; erro?: string }>, sucesso: string) {
    if (ocupado) return;
    setOcupado(true);
    setAviso(null);
    const r = await fn();
    setOcupado(false);
    if (r.ok) {
      setAviso({ tipo: "ok", texto: sucesso });
      await recarregar();
    } else {
      setAviso({ tipo: "erro", texto: r.erro || "Não deu. Tente de novo." });
    }
  }

  // T12.9 · O `id` cru só aparece depois de as listas de nomes chegarem e NENHUMA delas conhecer
  // esta instituição — que é dado inconsistente e tem de ficar visível. Antes disso, dizer que o
  // nome está vindo é a verdade.
  const nomeDa = (id: string) => {
    const achado =
      ativas.find((i) => i.id === id)?.nome ??
      pedidas.find((i) => i.id === id)?.nome ??
      // 08/10: o estudante pode pedir antes de a escola existir no banco; o nome vai no pedido.
      (vinculos ?? []).find((v) => v.instituicaoId === id && v.instituicaoNome)?.instituicaoNome;
    if (achado) return achado;
    return nomesProntos ? id : "Carregando o nome…";
  };


  return (
    <section className="settings-section inst-secao">
      <h2>Minha instituição</h2>

      {aviso && (
        <p className={`inst-aviso inst-aviso-${aviso.tipo}`} role="status">
          {aviso.texto}
        </p>
      )}

      {/* ── 1. MEUS VÍNCULOS ────────────────────────────────────────────────────────────── */}
      {vinculos === null ? (
        <p className="ajuda">Carregando…</p>
      ) : vinculos.length === 0 ? (
        <p className="ajuda">
          Você ainda não está vinculado a nenhuma escola. Estando vinculado, os quadrinhos da sua
          turma aparecem para você na biblioteca da instituição.
        </p>
      ) : (
        <ul className="inst-lista">
          {vinculos.map((v) => (
            <li key={v.id} className={`inst-item inst-${v.situacao}`}>
              <div>
                <strong>{nomeDa(v.instituicaoId)}</strong>
                <span className="inst-detalhe">
                  {v.papel === "educador" ? "Educador" : "Estudante"} ·{" "}
                  {ROTULO_SITUACAO_VINCULO[v.situacao]}
                </span>
                {v.situacao === "pendente" && v.papel === "aluno" && !ativas.some((i) => i.id === v.instituicaoId) && nomesProntos && (
                  <span className="inst-detalhe" data-testid="aviso-inativa">{AVISO_INSTITUICAO_INATIVA}</span>
                )}
              </div>

              {/* Recusado ou removido pode pedir de novo — T2.70, o aluno que é da escola e não
                  foi aceito por aquele professor. Sem esta saída, um engano é definitivo. */}
              {(v.situacao === "recusado" || v.situacao === "removido") && (
                <button
                  type="button"
                  className="botao-secundario"
                  disabled={ocupado}
                  onClick={() => {
                    const desde = entraNaFilaEm(v);
                    const quando =
                      desde && desde.getTime() > Date.now()
                        ? ` Ele entra na fila de moderação em ${desde.toLocaleDateString("pt-BR")}, 7 dias depois da recusa.`
                        : "";
                    comOcupado(() => pedirDeNovo(v), "Pedido enviado de novo." + quando);
                  }}
                >
                  Pedir de novo
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* ── 2. ENCONTRAR A ESCOLA NA BASE OFICIAL (T18.9) ─────────────────────────────────── */}
      {/* Regras de 06/10 (repo instituicoes-ensino): ninguém digita nome de escola. A busca
          substitui a lista só das ativas e o formulário de texto livre que ficavam aqui. */}
      {vinculos !== null && (
        <BuscaDeEscola
          pessoa={{ uid, nome: appUser.Name(), email: appUser.Email() }}
          meusVinculos={vinculos}
          ocupado={ocupado}
          executar={comOcupado}
        />
      )}

      {/* ── 3. AS ESCOLAS QUE EU PEDI PARA ATIVAR ──────────────────────────────────────────── */}
      {pedidas.length > 0 && (
        <div className="inst-bloco">
          <h3>Escolas que você pediu para ativar</h3>
          <ul className="inst-lista">
            {pedidas.map((i) => (
              <li key={i.id} className="inst-item inst-pendente">
                <div>
                  <strong>{i.nome}</strong>
                  <span className="inst-detalhe">{ROTULO_SITUACAO_INSTITUICAO[i.situacao]}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── 4. MODERAÇÃO ───────────────────────────────────────────────────────────────── */}
      {/* T2.78: "cai numa caixa de solicitação desse moderador, isso precisa ser composto como um
          pedido." E T2.75 diz que ela cai para TODOS os educadores ativos, não só para o
          primeiro — por isso a fila é por instituição, e não por quem a criou. */}
      {Object.keys(fila).length > 0 && (
        <div className="inst-bloco">
          <h3>Pedidos para entrar</h3>
          {Object.entries(fila).map(([instId, pedidos]) => (
            <div key={instId} className="inst-fila">
              <h4>{nomeDa(instId)}</h4>
              <ConvitesDaEscola instituicaoId={instId} educadorUid={uid} />
              {pedidos.length === 0 ? (
                <p className="ajuda">Nenhum pedido esperando.</p>
              ) : (
                <ul className="inst-lista">
                  {pedidos.map((p) => (
                    <li key={p.id} className="inst-item">
                      <div>
                        <strong>{p.nome || p.email || p.uid}</strong>
                        <span className="inst-detalhe">
                          quer entrar como {p.papel === "educador" ? "educador" : "estudante"}
                        </span>
                      </div>
                      <span className="inst-acoes">
                        <button
                          type="button"
                          className="botao-secundario"
                          disabled={ocupado}
                          onClick={() =>
                            comOcupado(
                              () => moderarVinculo(p.id, "ativo", uid),
                              "Pedido aceito."
                            )
                          }
                        >
                          Aceitar
                        </button>
                        <button
                          type="button"
                          className="botao-sair"
                          disabled={ocupado}
                          onClick={() =>
                            comOcupado(
                              () => moderarVinculo(p.id, "recusado", uid),
                              "Pedido recusado."
                            )
                          }
                        >
                          Recusar
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {(membros[instId] ?? []).length > 0 && (
                <>
                  <h4 className="inst-subtitulo">Quem já está na escola</h4>
                  <ul className="inst-lista" data-testid="membros-da-escola">
                    {(membros[instId] ?? []).map((m) => (
                      <li key={m.id} className="inst-item">
                        <div>
                          <strong>{m.nome || m.email || m.uid}</strong>
                          <span className="inst-detalhe">{m.papel === "educador" ? "educador" : "estudante"}</span>
                        </div>
                        {removendo === m.id ? (
                          <span className="inst-acoes" role="group" aria-label="Confirmar remoção">
                            <span className="inst-detalhe">Remover da escola? Fica registrado que foi você.</span>
                            <button
                              type="button"
                              className="botao-sair"
                              disabled={ocupado}
                              onClick={() => {
                                setRemovendo(null);
                                void comOcupado(() => moderarVinculo(m.id, "removido", uid), "Removido da escola.");
                              }}
                            >
                              Sim, remover
                            </button>
                            <button type="button" className="botao-secundario" disabled={ocupado} onClick={() => setRemovendo(null)}>
                              Cancelar
                            </button>
                          </span>
                        ) : (
                          <span className="inst-acoes">
                            <button type="button" className="botao-sair" disabled={ocupado} onClick={() => setRemovendo(m.id)}>
                              Remover
                            </button>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default MinhaInstituicao;
